import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  ArrowLeft,
  Briefcase,
  Users,
  CalendarBlank,
  CheckCircle,
  Clock,
  Download,
  CaretDown,
} from "phosphor-react";
import { formatCurrency } from "../utils/formatters";
import { format, subDays } from "date-fns";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function ReportsWork() {
  const navigate = useNavigate();
  const [jobsOverview, setJobsOverview] = useState(null);
  const [techProductivity, setTechProductivity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState({
    start: format(subDays(new Date(), 30), "yyyy-MM-dd"),
    end: format(new Date(), "yyyy-MM-dd"),
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const startISO = new Date(dateRange.start).toISOString();
      const endISO = new Date(dateRange.end + "T23:59:59").toISOString();
      
      const [jobsRes, techRes] = await Promise.all([
        fetch(`${API}/reports/jobs-overview?start_date=${startISO}&end_date=${endISO}`, { credentials: "include" }),
        fetch(`${API}/reports/tech-productivity?start_date=${startISO}&end_date=${endISO}`, { credentials: "include" }),
      ]);
      
      if (jobsRes.ok) setJobsOverview(await jobsRes.json());
      if (techRes.ok) setTechProductivity(await techRes.json());
    } catch (error) {
      console.error("Error fetching work reports:", error);
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const exportTechCSV = () => {
    if (!techProductivity?.technicians?.length) return;
    
    const headers = ["Technician", "Email", "Visits Completed", "Jobs Worked", "Revenue Attributed"];
    const rows = techProductivity.technicians.map((tech) => [
      tech.name,
      tech.email,
      tech.visits_completed,
      tech.jobs_worked,
      tech.revenue_attributed,
    ]);
    
    const csv = [headers, ...rows].map((row) => row.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `tech-productivity-${format(new Date(), "yyyy-MM-dd")}.csv`;
    a.click();
  };

  if (loading && !jobsOverview) {
    return (
      <div className="space-y-6" data-testid="reports-work-page">
        <div className="animate-pulse">
          <div className="h-8 bg-zinc-200 rounded w-48 mb-6" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-24 bg-zinc-200 rounded" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  const statusBreakdown = jobsOverview?.status_breakdown || {};
  const totalJobs = Object.values(statusBreakdown).reduce((a, b) => a + b, 0);

  return (
    <div className="space-y-6" data-testid="reports-work-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate("/reports")} data-testid="back-to-reports">
            <ArrowLeft size={18} />
          </Button>
          <div>
            <h1 className="font-heading text-2xl font-bold">Work Reports</h1>
            <p className="text-sm text-zinc-500">Jobs, visits, and technician productivity</p>
          </div>
        </div>
        
        {/* Date Range Selector */}
        <div className="flex items-center gap-2 bg-zinc-50 border border-zinc-200 rounded-lg p-2">
          <CalendarBlank size={18} className="text-zinc-500" />
          <input
            type="date"
            value={dateRange.start}
            onChange={(e) => setDateRange((prev) => ({ ...prev, start: e.target.value }))}
            className="bg-transparent border-none text-sm focus:outline-none"
          />
          <span className="text-zinc-400">to</span>
          <input
            type="date"
            value={dateRange.end}
            onChange={(e) => setDateRange((prev) => ({ ...prev, end: e.target.value }))}
            className="bg-transparent border-none text-sm focus:outline-none"
          />
        </div>
      </div>

      {/* Jobs Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="border-2 border-blue-200 bg-blue-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1">
              <Briefcase size={18} className="text-blue-600" />
              <span className="text-xs text-blue-700 uppercase font-medium">Jobs Created</span>
            </div>
            <p className="font-heading text-2xl font-bold text-blue-800">
              {jobsOverview?.summary?.total_created || 0}
            </p>
          </CardContent>
        </Card>
        
        <Card className="border-2 border-green-200 bg-green-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1">
              <CheckCircle size={18} className="text-green-600" />
              <span className="text-xs text-green-700 uppercase font-medium">Completed</span>
            </div>
            <p className="font-heading text-2xl font-bold text-green-800">
              {jobsOverview?.summary?.completed_in_period || 0}
            </p>
          </CardContent>
        </Card>
        
        <Card className="border-2 border-amber-200 bg-amber-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1">
              <Clock size={18} className="text-amber-600" />
              <span className="text-xs text-amber-700 uppercase font-medium">Completion Rate</span>
            </div>
            <p className="font-heading text-2xl font-bold text-amber-800">
              {jobsOverview?.summary?.completion_rate || 0}%
            </p>
          </CardContent>
        </Card>
        
        <Card className="border-2 border-purple-200 bg-purple-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1">
              <Users size={18} className="text-purple-600" />
              <span className="text-xs text-purple-700 uppercase font-medium">Active Techs</span>
            </div>
            <p className="font-heading text-2xl font-bold text-purple-800">
              {techProductivity?.summary?.total_technicians || 0}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Jobs Status Breakdown */}
      <Card className="border-2 border-zinc-200">
        <CardHeader className="pb-2">
          <CardTitle className="font-heading text-lg">Jobs by Status</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {[
              { key: "draft", label: "Draft", color: "bg-zinc-400" },
              { key: "scheduled", label: "Scheduled", color: "bg-blue-500" },
              { key: "in_progress", label: "In Progress", color: "bg-amber-500" },
              { key: "completed", label: "Completed", color: "bg-green-500" },
            ].map((status) => {
              const count = statusBreakdown[status.key] || 0;
              const percent = totalJobs > 0 ? (count / totalJobs) * 100 : 0;
              return (
                <div key={status.key} className="flex items-center gap-4">
                  <div className="w-24 text-sm text-zinc-600">{status.label}</div>
                  <div className="flex-1 h-2 bg-zinc-200 rounded-full overflow-hidden">
                    <div 
                      className={`h-full ${status.color} transition-all`}
                      style={{ width: `${percent}%` }}
                    />
                  </div>
                  <div className="w-20 text-right">
                    <span className="font-medium">{count}</span>
                    <span className="text-zinc-400 text-sm ml-1">({percent.toFixed(0)}%)</span>
                  </div>
                </div>
              );
            })}
          </div>
          
          {/* Invoicing breakdown */}
          <div className="mt-6 pt-4 border-t border-zinc-200">
            <h4 className="text-sm font-medium text-zinc-600 mb-3">Completed Jobs Invoicing</h4>
            <div className="flex gap-4">
              <div className="flex-1 bg-green-50 border border-green-200 rounded-lg p-3">
                <p className="text-sm text-green-700">Invoiced</p>
                <p className="font-heading text-xl font-bold text-green-800">{jobsOverview?.invoicing?.invoiced || 0}</p>
              </div>
              <div className="flex-1 bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-sm text-amber-700">Uninvoiced</p>
                <p className="font-heading text-xl font-bold text-amber-800">{jobsOverview?.invoicing?.uninvoiced || 0}</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Technician Productivity Table */}
      <Card className="border-2 border-zinc-200">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="font-heading text-lg">Technician Productivity</CardTitle>
            <Button 
              variant="outline" 
              size="sm"
              onClick={exportTechCSV}
              data-testid="export-tech-csv"
            >
              <Download size={14} className="mr-1" /> Export CSV
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {techProductivity?.technicians?.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="tech-productivity-table">
                <thead>
                  <tr className="text-left text-xs text-zinc-500 uppercase border-b border-zinc-200">
                    <th className="pb-3 font-medium">Technician</th>
                    <th className="pb-3 font-medium text-center">Visits</th>
                    <th className="pb-3 font-medium text-center">Jobs</th>
                    <th className="pb-3 font-medium text-right">Revenue</th>
                  </tr>
                </thead>
                <tbody>
                  {techProductivity.technicians.map((tech, idx) => (
                    <tr key={tech.technician_id} className="border-b border-zinc-100 hover:bg-zinc-50">
                      <td className="py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-zinc-200 rounded-full flex items-center justify-center text-xs font-medium">
                            {idx + 1}
                          </div>
                          <div>
                            <p className="font-medium">{tech.name}</p>
                            <p className="text-xs text-zinc-500">{tech.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="py-3 text-center">
                        <span className="font-medium">{tech.visits_completed}</span>
                      </td>
                      <td className="py-3 text-center">
                        <span className="font-medium">{tech.jobs_worked}</span>
                      </td>
                      <td className="py-3 text-right">
                        <span className="font-medium">{formatCurrency(tech.revenue_attributed)}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8 text-zinc-500">
              <Users size={32} className="mx-auto mb-2 opacity-50" />
              <p>No technician activity in this period</p>
            </div>
          )}
          
          {/* Summary row */}
          {techProductivity?.summary && (
            <div className="mt-4 pt-4 border-t border-zinc-200 flex justify-between items-center text-sm">
              <span className="text-zinc-500">Total ({techProductivity.summary.total_technicians} technicians)</span>
              <div className="flex gap-6">
                <span>
                  <strong>{techProductivity.summary.total_visits_completed}</strong> visits
                </span>
                <span>
                  <strong>{formatCurrency(techProductivity.summary.total_revenue_attributed)}</strong> revenue
                </span>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
